import React, { Children, cloneElement, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { VStack } from "IZOX/_X";
import { Accessor, LogicX, ZArray } from "IZOX/STATIC";
import { QFText, QFPassword, QFCheckBox, 
  QFDigitBox, QFDropUpload, QFDate, QFDropdown, 
  QFTabRadio, QFRadio, QFFile, QFSignature,
  QFDateRange, QFFreeTextTag, 
  QFError,
  QFNumber
} from "./_gears";
import _ from "lodash";

/**
 * GetValue: () => void
 * Clear: ()
 * SetValue: (formValue) => void
 * RemoveArrayIdx: (name, idx) => void,
 * AddArrayIdx: (name, value) => void
 * @typedef {import("IZOX/_X/XDiv").xdivparams}  xdivparams
 * @typedef {import("IZOX/_X/VStack").vstackparams} vstackparams
 * @typedef {import("IZOX/STATIC/ZValidate").fvalidate} fvalidate
 * @typedef {import("IZOX/__Typedef/common").lang} lang
 * 
 * @typedef {Object} validators
 * @property {String} name
 * @property {lang} label
 * @property {fvalidate | fvalidate[]} validators
 * 
 * @typedef {Object} qformparamsAddOns
 * @property {(callbacks) => void} onMounted
 * @property {any} value
 * @property {(formValue: any, name: String, value: *) => void} onChange
 * @property {Boolean} readOnly
 * @property {Boolean} performanceBoost
 * @property {(vList: validators[]) => void} onValidateReady
 * @property {*} defaultValue
 * 
 * @typedef {qformparamsAddOns & vstackparams & xdivparams} qformparams
 * 
 * @param {qformparams} param0
 */
function QForm({
  onMounted = null,
  children = null,
  value = null,
  onChange = null,
  readOnly = false,
  performanceBoost = true,
  defaultValue = null,
  onValidateReady = null,
  ctrls = null,
  ...others}){

  const [formValue, setFormValue] = useState(value ?? defaultValue ?? {});
  const [updateVia, setUpdateVia] = useState("default");
  const [updateField, setUpdateField] = useState(null);
  const [forceUpdateField, setForceUpdateField] = useState([]);
  const [validators, setValidators] = useState([]);
  const [vlistLength, setVlistLength] = useState(0);

  useEffect(() => {
    setUpdateVia("default");
    setFormValue(value ?? formValue ?? {});
  }, [value, formValue]);

  useEffect(() => {
    const _getValue = () => {
      return formValue;
    };

    const _getValidators = () => {
      return validators;
    };

    const _clear = (defaultForm) => {
      setFormValue(defaultForm ?? {});
      onChange?.(defaultForm ?? {});
    };

    const _setValue = (name, value) => {
      let _newFV = {...formValue};
      Accessor.Set(_newFV, name, value);
      setFormValue(_newFV);
      onChange?.(_newFV);
    };

    const _removeArrayIdx = (name, idx) => {
      let target = Accessor.Get(formValue, name);
      if(target){
        target = ZArray.DeleteIdx(target, idx);
        _setValue(name, target);
      }
    };

    const _addArrayIdx = (name, dvalue) => {
      let target = Accessor.Get(formValue, name);
      target = [...target, dvalue];
      _setValue(name, target);
    };

    let callbacks = {
      GetValue: _getValue,
      GetValidators: _getValidators,
      Clear: _clear,
      SetValue: _setValue,
      RemoveArrayIdx: _removeArrayIdx,
      AddArrayIdx: _addArrayIdx
    };

    onMounted?.(callbacks);
  }, [onMounted, formValue, onChange, validators]);

  useImperativeHandle(ctrls, () => ({
    GetValue: () => formValue,
    GetValidators: () => validators,
    Clear: (defaultForm) => {
      setFormValue(defaultForm ?? {});
      onChange?.(defaultForm ?? {});
    },
    SetValue: (name, value) => {
      setFormValue((prev) => {
        let _newFV = {...prev};
        Accessor.Set(_newFV, name, value);
        onChange?.(_newFV);
        return _newFV;
      });
    },
    RemoveArrayIdx: (name, idx) => {
      setFormValue((prev) => {
        let target = Accessor.Get(prev, name);
        if(target){
          target = ZArray.DeleteIdx(target, idx);
          let _newFV = {...prev};
          Accessor.Set(_newFV, name, target);
          onChange?.(_newFV);
          return _newFV;
        }
      });
    },
    AddArrayIdx: (name, dvalue) => {
      setFormValue((prev) => {
        let target = Accessor.Get(prev, name);
        target = [...target, dvalue];
        let _newFV = {...prev};
        Accessor.Set(_newFV, name, target);
        onChange?.(_newFV);
        return _newFV;
      });
    }
  }), [formValue, onChange, validators]);

  const _onValidateReady = useCallback((_newValidators) => {
    onValidateReady?.(_newValidators);
  }, [onValidateReady]);

  useEffect(() => {
    
    let _validators = [];
    const GetValidators = (_c, _v) => {
      if(_.isFunction(_c)) return;
      Children.map(_c, (o, i) => {
        if (!React.isValidElement(o)) return;
        if(o?.props?.children){
          GetValidators(o?.props?.children, _v);
        }
        if(LogicX.isOneOf(o.type, [QFText, QFPassword, 
          QFCheckBox, QFDigitBox, QFDropUpload, QFDate, QFDateRange,
          QFDropdown, QFTabRadio, QFRadio, QFFile, QFSignature, 
          QFNumber,
          QFFreeTextTag])){
          if(o.props.validators){
            _v.push({
              name: o.props.name,
              label: o.props.label,
              validators: o.props.validators
            });
          }
        }
      });
    };
    GetValidators(children, _validators);
    if(_validators.length !== vlistLength){
      setVlistLength(_validators.length);
      setValidators(_validators);
      _onValidateReady(_validators);
    }
  }, [children, _onValidateReady, vlistLength]);

  const _onSetDefault = useCallback((name, value) => {
    setFormValue((prev) => {
      if(_.isUndefined(Accessor.Get(prev, name))){
        let _newFV = _.cloneDeep(prev);
        Accessor.Set(_newFV, name, value);
        setUpdateField(name);
        setForceUpdateField([name]);
        return _newFV;
      }
      return prev;
    });
  }, []);

  const _onChange = useCallback((name, value, _force = [], callback) => {
    setFormValue((prev) => {
      if(_.isFunction(value)){
        value = value?.(Accessor.Get(prev, name), prev);
      }
      let _newFV = _.cloneDeep(prev);
      Accessor.Set(_newFV, name, value);
      onChange?.(_newFV, name, value);
      callback?.(_newFV, name, value);
      setUpdateField(name);
      setUpdateVia("update");
      setForceUpdateField(_force);
      return _newFV;
    });
  }, [onChange]);

  function renderChildren(_c){
    if(_.isFunction(_c)) return _c;
    return Children.map(_c, (o, i) => {
      if (!React.isValidElement(o)) return o;
      let addOns = {
        children: renderChildren(o?.props?.children),
      };
      if(LogicX.isOneOf(o.type, [QFText, QFPassword, 
          QFCheckBox, QFDigitBox, QFDropUpload, QFDate, QFDateRange,
          QFDropdown, QFTabRadio, QFRadio, QFFile, QFSignature, QFNumber,
          QFFreeTextTag, QFError])){
        addOns = {
          ...addOns,
          _onSetDefault: _onSetDefault,
          _onChange: _onChange,
          formValue: formValue,
          readOnly: readOnly || o?.props?.readOnly,
          performanceBoost: performanceBoost,
          updateVia: updateVia,
          updateField: updateField,
          forceUpdateField: forceUpdateField
        };
      }
      return cloneElement(o, addOns);
    });
  }

  return (
    <VStack 
      {...others}>
      {renderChildren(children)}
    </VStack>
  );
}

export default QForm;